import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import {
    ChangeEvent,
    HTMLInputAutoCompleteAttribute,
    useCallback,
    useRef,
    useState
} from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Noop,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";
import { components, GroupBase, InputProps, SingleValue } from "react-select";

import { MenuComponent } from "@/components/Form/Base/components/selectMenu.component";
import { MenuListComponent } from "@/components/Form/Base/components/selectMenuList.component";
import { Select } from "@/components/Form/Base/select.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

export enum LabelType {
    DIVIDER = "DIVIDER"
}

export type BaseOption<T> = {
    label: string | LabelType;
    value?: T;
    isDisabled?: boolean;
};

type SelectFieldProps<
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
> = FormValuesExploded<T> & {
    optionList: Array<TOption>;
    onChange?: (value: PathValue<T, Path<T>>) => void;
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    "aria-label"?: string;
};

const SelectField = <
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
>({
    placeholder,
    autoComplete,
    onChange,
    optionList,
    disabled,
    "aria-label": ariaLabel,
    ...baseFieldProps
}: SelectFieldProps<T, TOption>) => {
    const { t } = useGranite();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const { setValue, trigger } = useFormContext<T>();

    const [isFocussed, setIsFocussed] = useState(false);

    const getValue = useCallback(
        (
            value: ControllerRenderProps<T, Path<T>>["value"]
        ): SingleValue<TOption> | undefined => {
            if (!isDefined(value)) return undefined;

            return optionList.find((option) => option.value === value);
        },
        [optionList]
    );

    const handleOnChange = useCallback(
        (option: SingleValue<TOption>) => {
            setValue(
                baseFieldProps.name,
                (option ? option.value : null) as PathValue<T, Path<T>>,
                {
                    shouldValidate: false,
                    shouldDirty: true
                }
            );

            if (onChange && option?.value) {
                onChange(option?.value);
            }

            trigger(baseFieldProps.name);
        },
        [baseFieldProps.name, onChange, setValue, trigger]
    );

    const handleOnFocus = useCallback(() => setIsFocussed(true), []);

    const handleOnBlur = useCallback((onBlurCb: Noop) => {
        setIsFocussed(false);
        onBlurCb();
    }, []);

    const InputComponent = useCallback(
        (props: InputProps<TOption, false, GroupBase<TOption>>) => {
            const handleOnInputChange = (
                event: ChangeEvent<HTMLInputElement>
            ) => {
                const value = event.target.value;

                const newOption = optionList.find(
                    (option) => option.value === value
                );

                const autofilled = attempt(
                    () => inputRef.current?.matches(":autofill"),
                    false
                );

                if (autofilled) {
                    if (!newOption) return;

                    handleOnChange(newOption);
                } else {
                    props.onChange?.(event);
                }
            };

            return (
                <components.Input
                    {...props}
                    innerRef={(element) => {
                        inputRef.current = element;
                        props.innerRef?.(element);
                    }}
                    autoComplete={autoComplete}
                    onChange={handleOnInputChange}
                />
            );
        },
        [autoComplete, optionList, handleOnChange]
    );

    const renderOption = (option: TOption) => {
        if (option.label === LabelType.DIVIDER) {
            return <hr />;
        }

        return option.label;
    };

    return (
        <BaseField<T, Path<T>> {...baseFieldProps}>
            {({ field, fieldState }) => {
                return (
                    <Select<TOption>
                        {...field}
                        id={field.name}
                        instanceId={field.name}
                        inputId={field.name}
                        openMenuOnFocus={true}
                        classNamePrefix="select-box"
                        isInvalid={!!fieldState.isInvalid}
                        isValid={!!fieldState.isValid}
                        placeholder={isFocussed ? "" : placeholder}
                        options={optionList}
                        isSearchable={true}
                        components={{
                            Input: InputComponent,
                            Menu: MenuComponent,
                            MenuList: MenuListComponent
                        }}
                        blurInputOnSelect={true}
                        onBlur={() => handleOnBlur(field.onBlur)}
                        onFocus={handleOnFocus}
                        isMulti={false}
                        value={getValue(field.value)}
                        noOptionsMessage={() =>
                            t("general.selectBox.no.options")
                        }
                        onChange={handleOnChange}
                        isDisabled={disabled}
                        aria-label={ariaLabel}
                        formatOptionLabel={renderOption}
                    />
                );
            }}
        </BaseField>
    );
};

export { SelectField };
